import type { MouseEvent } from "react";
import { Facebook, Instagram } from "react-feather";
import { useNavigate } from "react-router-dom";

const SOCIAL_LINKS = [
  { label: "Facebook", href: "https://www.facebook.com/mycouponbook/", Icon: Facebook },
  { label: "Instagram", href: "https://www.instagram.com/mycouponbook/", Icon: Instagram },
];

// Same badge artwork as the landing hero's StoreButtons.
const APP_BADGES = [
  {
    label: "Download on the App Store",
    href: "https://couponbook.app.link/BsnZ5PEfH2b",
    src: "/images/AppleLogo.webp",
  },
  {
    label: "Get it on Google Play",
    href: "https://couponbook.app.link/BsnZ5PEfH2b",
    src: "/images/rectangle-34625679.webp",
  },
];

type FooterLink = { label: string; to?: string };

// `to` can be an internal path (optionally with a #section), a mailto: link, or a full https:// URL.
const FOOTER_COLUMNS: { title: string; links: FooterLink[] }[] = [
  {
    title: "About",
    links: [
      { label: "Company", to: "/about" },
      { label: "Who we are", to: "/about" },
      { label: "Service", to: "/about" },
      { label: "Sitemap", to:"https://www.mycouponbook.ca/sitemap.xml" },
    ],
  },
  {
    title: "Customer",
    links: [
      { label: "Home", to: "/" },
      { label: "Blog", to: "/blogs" },
      { label: "Map", to: "/coupons-map" },
      { label: "Online Brands", to: "/online-brands-list" },
      { label: "Features", to: "/landing" },
      { label: "Coupons", to: "/coupons-list" },
    ],
  },
  {
    title: "Business",
    links: [
      { label: "Join as a Business", to: "/business" },
      { label: "Launch With Us", to: "/launch-canada" },
      { label: "Business Login", to: "/login" },
      { label: "Success Stories", to: "/business#brands" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Help Center", to: "/business#faq" },
      { label: "Contact Us", to: "mailto:info@mycouponbook.ca" },
      { label: "FAQs", to: "/landing#faq" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Service", to: "/terms-of-service/customer" },
      // No standalone cookie policy yet — cookies are covered in the privacy policy.
      { label: "Cookie Policy", to: "/privacy-policy" },
    ],
  },
];

// Scroll to #id once it exists — landing sections render after data loads,
// so a freshly navigated page may not have the element on the first frame.
const scrollToSection = (id: string, attemptsLeft = 40) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (attemptsLeft > 0) {
    setTimeout(() => scrollToSection(id, attemptsLeft - 1), 100);
  }
};

export const Footer = () => {
  const navigate = useNavigate();

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
    // Let the browser handle mailto:, absolute URLs and modified clicks (new tab, etc.)
    if (/^(mailto:|https?:)/.test(to) || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();

    const [path, hash] = to.split("#");
    if (path !== window.location.pathname) {
      navigate(path);
      if (!hash) window.scrollTo(0, 0);
    }
    if (hash) scrollToSection(hash);
  };

  return (
    <footer className="w-full flex justify-center bg-[#F5F5F5] pt-20 pb-10">

      <div className="w-full max-w-[1700px] px-6">

        {/* TOP SECTION */}
        <div className="flex flex-col lg:flex-row justify-between gap-16">

          {/* LEFT */}
          <div className="w-full max-w-[480px] lg:w-[400px] xl:w-[480px] shrink-0">

            {/* Logo */}
            <img
              src="/images/mycb.webp"
              alt="Couponbook logo"
              className="h-[60px] mb-6"
            />


            <p className="text-[18px] text-[#222] mb-4">
              Contact for more details
            </p>


            {/* email input — button sits inset inside the pill (p-1.5) so it never gets clipped */}
            <div className="flex items-center gap-2 bg-white rounded-full shadow-sm w-full max-w-[480px] p-1.5">

              <input
                type="email"
                placeholder="enter your email"
                className="
                  flex-1
                  min-w-0
                  pl-4
                  py-2
                  bg-transparent
                  border-0
                  outline-none
                  text-[16px]
                "
              />

              <button
                type="button"
                className="
                  shrink-0
                  px-6
                  py-2.5
                  text-white
                  text-[15px]
                  font-medium
                  bg-gradient-to-r
                  from-red-600
                  to-red-700
                  border-0
                  rounded-full
                  hover:from-red-700
                  hover:to-red-800
                  transition-colors
                "
              >
                Contact
              </button>

            </div>

            {/* Social links */}
            <p className="text-[16px] font-semibold text-[#222] mt-8 mb-3">Follow us</p>
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="
                    flex items-center justify-center
                    w-11 h-11
                    rounded-full
                    bg-white
                    text-[#222]
                    shadow-sm
                    hover:bg-gradient-to-r hover:from-red-600 hover:to-red-700 hover:text-white
                    transition-colors
                  "
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>

            {/* App download badges */}
            <p className="text-[16px] font-semibold text-[#222] mt-8 mb-3">Get the app</p>
            <div className="flex flex-wrap items-center gap-3">
              {APP_BADGES.map(({ label, href, src }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="
                    flex items-center justify-center
                    w-[170px] h-12
                    bg-black
                    rounded-[30px]
                    hover:opacity-90
                    transition-opacity
                  "
                >
                  <img src={src} alt={label} className="w-[130px] h-[32px] object-contain" />
                </a>
              ))}
            </div>

          </div>



          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-3 xl:grid-cols-5 gap-x-10 gap-y-12 xl:gap-x-16 text-[#222]">

            {FOOTER_COLUMNS.map((column) => (
              <div key={column.title}>
                <h4 className="font-semibold mb-4 text-[18px]">{column.title}</h4>

                <ul className="space-y-3 text-[16px] text-gray-600">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      {link.to ? (
                        <a
                          href={link.to}
                          onClick={(event) => handleLinkClick(event, link.to as string)}
                          className="text-gray-600 no-underline hover:text-[#D90000] transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        link.label
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </div>



        {/* DIVIDER */}
        <div className="border-t mt-16 pt-6 text-center text-gray-500 text-[15px]">

          © 2026 My Coupon Book. Made with ❤️ in Canada.

        </div>

      </div>

    </footer>
  );
};